import { t, localizedName } from "../utils/i18n.ts";
import type { Card } from "../entities/types.ts";
import type { EventBus } from "../core/EventBus.ts";
import { COLORS } from "../core/constants.ts";
import { UI_Z, UI_EASE, roleFontSize } from "./uiTokens.ts";
import { getPersistenceTag } from "./cardPersistence.ts";
import { SkillCard } from "./SkillCard.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { makeKeyboardActivatable } from "./a11y.ts";

export type DraftSelectCallback = (card: Card) => void;
export type DraftSkipCallback = () => void;

interface CardEntry {
  sc: SkillCard;
  card: Card;
}

export class DraftUI {
  private container: HTMLDivElement;
  private onSelect: DraftSelectCallback | null = null;
  private onSkip: DraftSkipCallback | null = null;
  private visible_ = false;
  private entries: CardEntry[] = [];
  private timers: ReturnType<typeof setTimeout>[] = [];
  private bus: EventBus | null;

  constructor(bus?: EventBus) {
    this.bus = bus ?? null;
    this.container = document.createElement("div");
    this.container.id = "draft-overlay";
    // Mount inside the fixed-stage container so the draft overlay scales with
    // the game (S183/BL-201: position:absolute fills the stage frame exactly). The
    // fly-to-rack clone below stays on <body>: it works in viewport space from
    // getBoundingClientRect, which already reflects the stage transform. Falls
    // back to <body> in headless/test DOM with no stage.
    (document.getElementById("game-container") ?? document.body).appendChild(
      this.container,
    );
    this.injectStyles();
  }

  get visible(): boolean {
    return this.visible_;
  }

  show(
    cards: Card[],
    onSelect: DraftSelectCallback,
    onSkip: DraftSkipCallback,
  ): void {
    this.onSelect = onSelect;
    this.onSkip = onSkip;
    this.visible_ = true;
    this.disposeEntries();
    this.clearTimers();
    this.container.innerHTML = "";
    this.container.classList.remove("has-legendary", "draft-shudder");

    // Banner
    const banner = document.createElement("div");
    banner.className = "draft-banner";
    banner.textContent = t("draft.banner");
    this.container.appendChild(banner);

    // The full-screen legendary wash + vignette (the paused-modal GPU spend).
    const wash = document.createElement("div");
    wash.className = "draft-wash";
    wash.setAttribute("aria-hidden", "true");
    this.container.appendChild(wash);

    // Card row — one Trainer card per pick (the shared SkillCard frame).
    const cardRow = document.createElement("div");
    cardRow.className = "draft-card-row";
    for (const card of cards) {
      const entry = this.createCard(card);
      this.entries.push(entry);
      cardRow.appendChild(entry.sc.el);
    }
    this.container.appendChild(cardRow);

    // Skip button
    const skipBtn = document.createElement("button");
    skipBtn.className = "draft-skip-btn";
    skipBtn.textContent = t("draft.skip");
    skipBtn.addEventListener("click", () => {
      if (this.onSkip) this.onSkip();
    });
    this.container.appendChild(skipBtn);

    this.container.classList.add("visible");

    // ⭐ THE MEGA-CATCH — the 3 picks deal in SEALED, telegraph their rarity, then
    // flip/decrypt into Trainer cards (rarity-staggered so the tension builds). A
    // legendary flip detonates the modal. Reduced-motion → instant-but-rich.
    requestAnimationFrame(() => {
      this.entries.forEach((entry, idx) => {
        entry.sc.playPull({
          delayMs: 120 + idx * 360,
          onFlip: () => this.onCardFlip(entry),
        });
      });
    });
  }

  /** A card has flipped into its face. A legendary detonates the whole modal:
   *  a gold wash + vignette + a screen-shudder, and the unpicked siblings drop
   *  into background depth-of-field so the gold card is the only thing alive. */
  private onCardFlip(entry: CardEntry): void {
    if (entry.card.rarity !== "legendary" || isReducedMotion()) return;
    if (this.container.classList.contains("has-legendary")) return;
    this.container.classList.add("has-legendary", "draft-shudder");
    for (const other of this.entries) {
      if (other !== entry) other.sc.el.classList.add("draft-card--dof");
    }
    this.schedule(() => {
      this.container.classList.remove("draft-shudder");
      for (const other of this.entries) {
        if (other !== entry) other.sc.el.classList.remove("draft-card--dof");
      }
    }, 1400);
  }

  hide(): void {
    // PT-37: signal the music director to lift the draft interlude. Guarded on
    // `visible_` so every dismissal path (select/skip/reset/return-to-menu)
    // emits exactly once and balances the `draft.cardsDrawn` that opened it.
    if (this.visible_) this.bus?.emit("draft.closed");
    this.visible_ = false;
    this.clearTimers();
    this.container.classList.remove("visible", "has-legendary", "draft-shudder");
    this.container.classList.add("closing");
    setTimeout(() => {
      this.container.classList.remove("closing");
      this.disposeEntries();
      this.container.innerHTML = "";
    }, 400);
  }

  private disposeEntries(): void {
    for (const e of this.entries) e.sc.destroy();
    this.entries = [];
  }

  private clearTimers(): void {
    for (const tmr of this.timers) clearTimeout(tmr);
    this.timers = [];
  }

  private schedule(fn: () => void, delayMs: number): void {
    this.timers.push(setTimeout(fn, delayMs));
  }

  private createCard(card: Card): CardEntry {
    const cardName = localizedName("card", card.id, card.name);
    const cardDesc = localizedName("card", `${card.id}.desc`, card.description);

    const sc = new SkillCard(card, {
      variant: "draft",
      name: cardName,
      description: cardDesc,
      extraClasses: ["draft-card"],
      // BL-296 — the draft card is the ONE surface with a genuinely large art
      // window; the default cap left a 72 dp emblem on a 630 dp card.
      heroRadius: 62,
    });
    const el = sc.el;

    // BL-110 — the draft pick is a core run decision: make the card keyboard-
    // operable (role=button + tabindex + Enter/Space mirroring the click) and
    // give it an AT name. The inner TradingCard visual stays aria-hidden
    // (decorative); this label carries the meaning.
    el.setAttribute("aria-label", `${cardName}: ${cardDesc}`);
    const activate = (): void => {
      if (el.classList.contains("selected")) return;

      // Lock the row, commit the pick, decay its siblings (§6.8).
      for (const other of this.entries) {
        other.sc.el.style.pointerEvents = "none";
        other.sc.el.classList.remove("draft-card--dof");
        if (other.sc === sc) other.sc.select();
        else other.sc.dismiss();
      }
      this.showInstalled();

      // CR-21: Tween-fly the chosen card toward its destination.
      const rect = el.getBoundingClientRect();
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.position = "fixed";
      clone.style.left = `${rect.left}px`;
      clone.style.top = `${rect.top}px`;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.zIndex = "9999";
      clone.style.transition = "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      clone.style.pointerEvents = "none";
      document.body.appendChild(clone);
      el.style.opacity = "0";

      const persistenceTag = getPersistenceTag(card);
      const isPayload = persistenceTag.kind === "payload";
      const isTowerTargeted =
        card.category === "offensive" || card.category === "defensive";
      const gameCanvas = document.getElementById("game-canvas");
      const payloadRack = document.getElementById("payload-rack-bar");
      let destLeft = "50%";
      let destTop = "90%";
      let destScale = 0.3;
      if (isPayload && payloadRack) {
        const rackRect = payloadRack.getBoundingClientRect();
        destLeft = `${rackRect.left + rackRect.width / 2}px`;
        destTop = `${rackRect.top + rackRect.height / 2}px`;
        destScale = 0.18; // shrink into a chip-sized footprint
        clone.style.boxShadow = `0 0 22px ${COLORS.AMBER_WARN}, 0 0 44px rgba(232, 208, 152,0.55)`;
        setTimeout(() => {
          payloadRack.classList.add("payload-rack--landing");
          setTimeout(
            () => payloadRack.classList.remove("payload-rack--landing"),
            450,
          );
        }, 360);
      } else if (isTowerTargeted && gameCanvas) {
        const canvasRect = gameCanvas.getBoundingClientRect();
        destLeft = `${canvasRect.left + canvasRect.width / 2}px`;
        destTop = `${canvasRect.top + canvasRect.height / 2}px`;
      }

      requestAnimationFrame(() => {
        clone.style.left = destLeft;
        clone.style.top = destTop;
        clone.style.transform = `translate(-50%, -50%) scale(${destScale})`;
        clone.style.opacity = "0.5";
      });
      setTimeout(() => clone.remove(), 450);

      setTimeout(() => {
        if (this.onSelect) this.onSelect(card);
      }, 500);
    };
    makeKeyboardActivatable(el, activate);

    return { sc, card };
  }

  /** The committed-pull stamp ("INSTALLED" / "PATCHED IN"). Reduced-motion-safe. */
  private showInstalled(): void {
    const stamp = document.createElement("div");
    stamp.className = "draft-installed";
    stamp.textContent = t("draft.installed");
    this.container.appendChild(stamp);
  }

  private injectStyles(): void {
    const style = document.createElement("style");
    style.textContent = `
      /* CR-9: heading pinned UP (flex-start + top pad), Discard pinned to the
         very bottom (absolute, below), so the freed middle band lets the cards
         render much larger — the owner's "Karten deutlich größer". */
      #draft-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column; align-items: center; justify-content: flex-start;
        padding-top: 54px;
        background: rgba(2, 2, 8, 0.55); z-index: ${UI_Z.DRAFT};
        opacity: 0; pointer-events: none; transition: opacity 0.4s ease, background 0.5s ease;
      }
      #draft-overlay.visible { opacity: 1; pointer-events: all; }
      #draft-overlay.closing { opacity: 0; pointer-events: none; }
      /* the legendary moment dims the whole modal so the gold card owns it. */
      #draft-overlay.has-legendary { background: rgba(1,1,4,0.82); }
      /* BL-298 — the header EXPLAINS the situation; it must not out-shout the
         thing being chosen. Measured before: 17.72 real px against the skill
         name's 18.45 — near parity — in a 58 dp band, with a 20px glow and a
         2s opacity pulse pulling the eye away from the cards. Now a quiet
         kicker: the heading role, tighter tracking, a third of the glow, no pulse,
         and 24 dp of the vertical budget handed back to the row. */
      .draft-banner {
        font-family: 'VT323', 'Press Start 2P', 'Courier New', monospace;
        font-size: ${roleFontSize("heading")}; font-weight: 700;
        color: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 74%, transparent);
        letter-spacing: 3px; margin-bottom: 14px;
        text-shadow: 0 0 7px rgba(136, 224, 208,0.28); text-transform: uppercase;
        padding: 5px 14px;
        border: 1px solid rgba(136, 224, 208,0.18); background: rgba(136, 224, 208,0.03);
        position: relative; z-index: 2;
      }
      @keyframes bannerPulse { 0%,100%{opacity:1} 50%{opacity:0.7} }

      /* the full-screen gold wash + vignette — off until a legendary flips. */
      .draft-wash {
        position: absolute; inset: 0; pointer-events: none; z-index: 1; opacity: 0;
        background:
          radial-gradient(circle at 50% 46%, rgba(255,194,61,0.22) 0%, transparent 55%),
          radial-gradient(circle at 50% 50%, transparent 52%, rgba(0,0,0,0.6) 100%);
        transition: opacity 0.5s ease;
      }
      #draft-overlay.has-legendary .draft-wash { opacity: 1; }
      #draft-overlay.draft-shudder { animation: draftShudder 0.5s ease; }
      @keyframes draftShudder {
        0%,100% { transform: translate(0,0); }
        20% { transform: translate(-3px,2px); }
        40% { transform: translate(3px,-2px); }
        60% { transform: translate(-2px,-2px); }
        80% { transform: translate(2px,2px); }
      }

      .draft-card-row { display: flex; gap: 40px; position: relative; z-index: 2; }
      /* The draft card box (the shared SkillCard frame fills it). BL-77b: roomier
         box + smaller art window so the (now larger) effect description reads in
         full and the codename no longer clips. CR-9: enlarged 248→360 (keeps the
         2:3 ratio + the art/box proportion) — 3×360 + 2×40 = 1160px, still fits a
         centred row at the narrowest in-band stage (4:3 iPad designW 1200). */
      /* BL-184 wrap guard on the draft card. CR-9b: the card box grew 248→360
         (×1.45) but the skill type scale (--tcs) defaulted to 1, so the TEXT
         stayed original-size in a bigger box ("zu klein, nutzt das Potenzial
         nicht"). Scale --tcs with the box so the whole card — banner, codename,
         effect text, footer — enlarges in proportion (cardFloor = max(floor,
         base × --tcs × --ui-scale)); art rides --tc-skill-art-h in step. */
      /* BL-296 — --tc-skill-art-max-h lets the hero window absorb the slack the
         copy compression freed (see TradingCard). The cap is ~1.7x the base so
         the three cards in a row never differ wildly in hero size, and what the
         cap leaves is split above/below the text stack. */
      .draft-card { width: 360px; height: 460px; --tcs: 1.45; --tc-skill-art-h: 128px; --tc-skill-art-min-h: 104px; --tc-skill-art-max-h: 184px; cursor: pointer; overflow-wrap: break-word; word-break: normal; }
      /* CR-9: the iPhone has the most horizontal slack (designW ~1944 at ~2.16
         aspect), and the owner asked for the cards to be biggest there — push
         them larger still on the phone tier (3×420 + 2×40 = 1340 < 1944), text
         scaled to match (420/248 ≈ 1.69). */
      html.phone-tier .draft-card { width: 420px; height: 536px; --tcs: 1.69; --tc-skill-art-h: 150px; --tc-skill-art-min-h: 120px; --tc-skill-art-max-h: 214px; }
      /* ══ BL-298 · the DECISION hierarchy on the draft card ══════════════
         Audited in real px at 852x393 (the review profile). The card's own
         ranking was INVERTED: the two facts that change the decision rendered
         SMALLER than the three that decorate it —

           SKILL NAME              18.45   ← the star, correct
           main effect headline    13.84
           effect description      12.91   ← nearly the headline: no hierarchy
           ⚡ SKILL banner          10.15
           CLASS-Ω PROTOCOL        10.15   ← lore, restating the category badge
           AXIOM · LEG 14/22       10.15   ← dossier taxonomy
           [L] intercepted…         9.22   ← flavour, restating the rarity
           ★ LEGENDARY (category)   9.22   ← DECISION-RELEVANT, second smallest
           PERMANENT / 3W           8.30   ← DECISION-RELEVANT, smallest of all

         So: the effect leads, the constraint and the category climb, and the
         lore that only repeats what a badge already says goes. Scoped to
         .draft-card — the binder / contender / swap-deck cards keep the full
         collectible dressing. */
      .draft-card .tc-move { font-size: max(12px, calc(17px * var(--tcs,1) * var(--ui-scale,1))); }
      .draft-card .tc-fx { font-size: max(12px, calc(13px * var(--tcs,1) * var(--ui-scale,1))); opacity: 0.88; }
      .draft-card .tc-persist { font-size: max(12px, calc(13px * var(--tcs,1) * var(--ui-scale,1))); padding: 2px 7px; letter-spacing: 1.4px; }
      .draft-card .tc-type { font-size: max(12px, calc(13px * var(--tcs,1) * var(--ui-scale,1))); padding: 2px 7px; }
      /* the lore layer: present, but never in the decision's way. */
      .draft-card .tc-sub { opacity: 0.5; letter-spacing: 1px; }
      /* [L] intercepted · archived restates the rarity the pips and the badge
         already carry, and AXIOM · LEG 14/22 is a collection number nobody
         drafts on. Both leave the DRAFT card; the pips stay (rarity IS part of
         the decision) and keep the footer rule as the card's closing line. */
      .draft-card .tc-flavour { display: none; }
      .draft-card .tc-foot-l { display: none; }
      .draft-card .tc-class { justify-content: flex-end; }

      /* ══ BL-298 · ELEVATION — the cards leave the playfield ═════════════
         Two shadow systems were rendered and compared on the real screen: a
         single wide ambient (reads as fog on OLED black, and bleeds into the
         neighbouring card across a 40 dp gap) and this two-part contact +
         ambient pair. The contact shadow is tight and directly under the card
         so the eye reads "resting on something"; the ambient is wide, very low
         alpha and offset DOWN only, so it separates without spilling sideways.
         Neither adds layout size — box-shadow is outside the box model, and the
         row keeps its 40 dp gutter. */
      .draft-card {
        box-shadow:
          0 6px 12px -4px rgba(0,0,0,0.85),
          0 22px 38px -18px rgba(0,0,0,0.7),
          inset 0 1px 0 color-mix(in srgb, var(--rarity-mid, ${COLORS.SENTINEL_GREEN}) 22%, transparent);
        transition: transform 140ms ${UI_EASE.settle}, box-shadow 140ms ${UI_EASE.settle};
      }
      /* rim-light: a hairline of the card's own accent along the top edge, so
         the elevation reads as a lit object rather than a pasted-on shadow. */
      .draft-card .tc-body { position: relative; }
      .draft-card:hover, .draft-card:focus-visible {
        transform: translateY(-3px);
        box-shadow:
          0 9px 16px -4px rgba(0,0,0,0.88),
          0 30px 52px -20px rgba(0,0,0,0.72),
          0 0 22px -6px color-mix(in srgb, var(--rarity-mid, ${COLORS.SENTINEL_GREEN}) 38%, transparent),
          inset 0 1px 0 color-mix(in srgb, var(--rarity-mid, ${COLORS.SENTINEL_GREEN}) 34%, transparent);
      }
      /* press: a short, physical settle — the card takes the pressure. */
      .draft-card:active {
        transform: translateY(1px) scale(0.994);
        box-shadow:
          0 3px 7px -3px rgba(0,0,0,0.9),
          0 12px 22px -14px rgba(0,0,0,0.6),
          inset 0 1px 0 color-mix(in srgb, var(--rarity-mid, ${COLORS.SENTINEL_GREEN}) 26%, transparent);
      }
      /* committed: the chosen card comes forward, the others stay set back. */
      .draft-card.selected {
        transform: translateY(-6px);
        box-shadow:
          0 12px 20px -4px rgba(0,0,0,0.9),
          0 38px 64px -22px rgba(0,0,0,0.75),
          0 0 30px -6px color-mix(in srgb, var(--rarity-mid, ${COLORS.SENTINEL_GREEN}) 46%, transparent);
      }
      @media (prefers-reduced-motion: reduce) {
        .draft-card .tc { transition: none; }
        .draft-card:hover .tc, .draft-card:active .tc, .draft-card.selected { transform: none; }
      }

      /* unpicked siblings drop into background depth-of-field on a legendary. */
      .draft-card--dof { filter: blur(3px) brightness(0.6); opacity: 0.55;
        transition: filter 0.4s ease, opacity 0.4s ease; }

      #payload-rack-bar.payload-rack--landing {
        animation: payloadRackLanding 0.45s ease-out;
      }
      @keyframes payloadRackLanding {
        0% { filter: brightness(2.2) drop-shadow(0 0 14px ${COLORS.AMBER_WARN}); }
        100% { filter: brightness(1); }
      }

      /* the committed-pull stamp. */
      .draft-installed {
        position: absolute; z-index: 50; top: 50%; left: 50%;
        transform: translate(-50%,-50%);
        font-family: 'VT323','Courier New',monospace; font-size: ${roleFontSize("display")};
        letter-spacing: 6px; color: ${COLORS.SENTINEL_GREEN}; text-transform: uppercase;
        text-shadow: 0 0 22px rgba(136, 224, 208,0.8), 0 0 4px #fff;
        pointer-events: none; animation: draftInstalled 0.7s cubic-bezier(0.34,1.56,0.64,1);
      }
      @keyframes draftInstalled {
        0% { opacity: 0; transform: translate(-50%,-50%) scale(0.4); }
        45% { opacity: 1; transform: translate(-50%,-50%) scale(1.12); }
        100% { opacity: 0.92; transform: translate(-50%,-50%) scale(1); }
      }

      /* CR-9: Discard pinned near the very bottom (out of flow) so the cards own
         the freed middle band; centred horizontally over the overlay. */
      .draft-skip-btn {
        position: absolute; bottom: 22px; left: 50%; transform: translateX(-50%);
        font-family:'VT323','Press Start 2P','Courier New',monospace;
        font-size:${roleFontSize("body")}; color:${COLORS.SENTINEL_GREEN}; background:rgba(0,40,20,0.8);
        border:1px solid #1a5a3a; padding:10px 24px; cursor:pointer; letter-spacing:2px;
        transition:all 0.2s; z-index: 3;
      }
      /* BL-298 — DISCARD is secondary and must not compete with the three skill
         decisions, but it stays findable and touch-safe. Quieter fill, dimmer
         text, no glow at rest; the hit area GROWS while the visual weight drops
         (measured before: 111.4 x 21.4 real px — under the touch floor). */
      .draft-skip-btn {
        color: color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 62%, transparent);
        background: rgba(0, 26, 16, 0.66);
        border-color: rgba(136, 224, 208, 0.16);
        letter-spacing: 1.5px; padding: 14px 30px;
      }
      .draft-skip-btn:hover { background:rgba(136, 224, 208,0.1); border-color:${COLORS.SENTINEL_GREEN}; box-shadow:0 0 15px rgba(136, 224, 208,0.3); color:#fff; }
      /* BL-109 — touch-target floor on the skip CTA (pointer:coarse pattern). */
      @media (pointer:coarse) { .draft-skip-btn { min-height: 44px; } }

      @media (prefers-reduced-motion: reduce) {
        #draft-overlay, .draft-banner, .draft-installed, .draft-card--dof,
        #draft-overlay.draft-shudder, .draft-wash { animation: none !important; transition: none; }
      }
    `;
    document.head.appendChild(style);
  }
}
